import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from "src/shared/shared.module";
import { LoadingDialogComponent } from 'src/shared/loading-dialog/loading-dialog.component';

declare const bootstrap: any;

const STATIC_MODAL_OPTIONS = {
  backdrop: 'static',
  keyboard: false,
};
const CLOSE_FALLBACK_MS = 500;

export interface ImportErrorItem {
  no: number;
  column: string;
  row: number;
  description: string;
}

export interface ImportErrorResponse {
  success: boolean;
  message: string;
  errors: ImportErrorItem[];
}

@Component({
  selector: 'app-form-import-new',
  imports: [SharedModule, TranslateModule, CommonModule],
  templateUrl: './form-import-new.component.html',
  styleUrl: './form-import-new.component.scss',
})
export class FormImportNewComponent {
  @Input() modalId = 'importDataModal';
  @Input() title = 'COMPONENT.IMPORT_DATA';
  @Input() dragText = 'ลากไฟล์มาวางที่นี่ หรือ คลิกเพื่อเลือกไฟล์';
  @Input() fileLabel = 'ไฟล์อัปโหลด';
  @Input() cancelText = 'COMPONENT.CANCEL';
  @Input() saveText = 'COMPONENT.SAVE';
  @Input() maxFileSizeMb = 2;
  @Input() allowedExtensions = ['.xlsx'];
  @Input() isSaving = false;
  @Input() loadingTitle = 'กำลังนำเข้าข้อมูล';
  @Input() loadingMessage = 'ระบบกำลังตรวจสอบและบันทึกข้อมูล กรุณารอสักครู่';

  @Output() saved = new EventEmitter<File>();
  @Output() closed = new EventEmitter<void>();

  @ViewChild('importModal')
  private importModal?: ElementRef<HTMLElement>;
  @ViewChild('importLoadingDialog')
  private importLoadingDialog?: LoadingDialogComponent;

  selectedFileName = '';
  fileError = '';
  selectedFile?: File;
  importError?: ImportErrorResponse;

  private reopenUploadOnErrorClose = true;

  private cdr = inject(ChangeDetectorRef);

  get modalLabelId(): string {
    return `${this.modalId}Label`;
  }

  get errorModalId(): string {
    return `${this.modalId}Error`;
  }

  get errorModalLabelId(): string {
    return `${this.errorModalId}Label`;
  }

  get loadingModalId(): string {
    return `${this.modalId}Loading`;
  }

  get acceptedFileTypes(): string {
    return this.allowedExtensions.join(',');
  }

  get helperText(): string {
    return `รองรับเฉพาะไฟล์นามสกุล ${this.allowedExtensions.join(', ')} ขนาดไม่เกิน ${this.maxFileSizeMb} MB`;
  }

  openModal(): void {
    const modalElement = this.getUploadModalElement();

    if (modalElement == null) {
      return;
    }

    bootstrap.Modal.getOrCreateInstance(modalElement, STATIC_MODAL_OPTIONS).show();
  }

  closeModal(): void {
    this.closeImportLoading();
    this.closeUploadModal();
  }

  closeAfterImportSuccess(afterClosed: () => void): void {
    this.closeImportLoading(() => {
      this.closeUploadModal(afterClosed);
    });
  }

  showImportError(response: unknown, options: { reopenUploadOnClose?: boolean } = {}): void {
    this.reopenUploadOnErrorClose = options.reopenUploadOnClose ?? true;
    this.importError = this.normalizeImportError(response);
    this.cdr.detectChanges();
    this.closeImportLoading(() => this.showImportErrorAfterLoadingClosed());
  }

  closeImportError(): void {
    const errorModalElement = document.getElementById(this.errorModalId);

    if (errorModalElement == null) {
      return;
    }

    if (this.reopenUploadOnErrorClose) {
      const uploadModalElement = document.getElementById(this.modalId);

      if (uploadModalElement == null) {
        return;
      }

      const errorModal = bootstrap.Modal.getInstance(errorModalElement)
        ?? new bootstrap.Modal(errorModalElement, STATIC_MODAL_OPTIONS);

      errorModalElement.addEventListener('hidden.bs.modal', () => {
        bootstrap.Modal.getOrCreateInstance(uploadModalElement, STATIC_MODAL_OPTIONS).show();
      }, { once: true });

      errorModal.hide();
      return;
    }

    this.clearFileSelection();
    this.closeModalElement(errorModalElement, () => this.removeOrphanedBackdrops());
  }

  clearFileSelection(): void {
    this.selectedFileName = '';
    this.fileError = '';
    this.selectedFile = undefined;
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    this.fileError = '';
    this.selectedFileName = '';
    this.selectedFile = undefined;
    if (file == null) return;

    const maxSize = this.maxFileSizeMb * 1024 * 1024;
    const fileName = file.name.toLowerCase();
    const isAllowedExtension = this.allowedExtensions.some((extension) =>
      fileName.endsWith(extension.toLowerCase())
    );

    this.selectedFileName = file.name;

    if (!isAllowedExtension) {
      this.fileError = `รองรับเฉพาะไฟล์นามสกุล ${this.allowedExtensions.join(', ')}`;
      return;
    }

    if (file.size > maxSize) {
      this.fileError = `ไฟล์มีขนาดใหญ่เกินไป กรุณาอัปโหลดไฟล์ขนาดไม่เกิน ${this.maxFileSizeMb} MB`;
      return;
    }

    this.selectedFile = file;
  }

  clearFile(fileInput: HTMLInputElement): void {
    this.clearFileSelection();
    fileInput.value = '';
  }

  onSave(): void {
    if (this.isSaving || this.selectedFile == null || this.fileError !== '') {
      return;
    }

    this.importError = undefined;
    this.openImportLoading();
    this.saved.emit(this.selectedFile);
  }

  onClose(): void {
    this.clearFileSelection();
    this.importError = undefined;
    this.closeImportLoading();
    this.closed.emit();
  }

  private showImportErrorAfterLoadingClosed(): void {
    const uploadModalElement = this.getUploadModalElement();
    const errorModalElement = document.getElementById(this.errorModalId);

    if (uploadModalElement == null || errorModalElement == null) {
      return;
    }

    const uploadModal = bootstrap.Modal.getInstance(uploadModalElement)
      ?? new bootstrap.Modal(uploadModalElement, STATIC_MODAL_OPTIONS);

    uploadModalElement.addEventListener('hidden.bs.modal', () => {
      bootstrap.Modal.getOrCreateInstance(errorModalElement, STATIC_MODAL_OPTIONS).show();
    }, { once: true });

    uploadModal.hide();
  }

  private normalizeImportError(response: unknown): ImportErrorResponse {
    if (response == null || typeof response !== 'object' || Array.isArray(response)) {
      return {
        success: false,
        message: '',
        errors: [],
      };
    }

    const importError = response as Record<string, unknown>;
    const rawErrors = Array.isArray(importError.errors) ? importError.errors : [];

    return {
      success: importError.success === true,
      message: typeof importError.message === 'string' ? importError.message : '',
      errors: rawErrors.map((error) => {
        const item = error !== null && typeof error === 'object' && !Array.isArray(error)
          ? error as Record<string, unknown>
          : {};

        return {
          no: Number(item.no),
          column: typeof item.column === 'string' ? item.column : '',
          row: Number(item.row),
          description: typeof item.description === 'string' ? item.description : '',
        };
      }),
    };
  }

  private getUploadModalElement(): HTMLElement | null {
    return this.importModal?.nativeElement ?? document.getElementById(this.modalId);
  }

  private openImportLoading(): void {
    this.importLoadingDialog?.open();
  }

  private closeUploadModal(afterHidden?: () => void): void {
    const modalElement = this.getUploadModalElement();

    if (modalElement == null) {
      afterHidden?.();
      return;
    }

    this.closeModalElement(modalElement, afterHidden);
  }

  private closeModalElement(modalElement: HTMLElement, afterHidden?: () => void): void {
    this.blurFocusedElementInside(modalElement);
    const modal = bootstrap.Modal.getInstance(modalElement) ?? new bootstrap.Modal(modalElement, STATIC_MODAL_OPTIONS);
    let completed = false;
    const complete = () => {
      if (completed) {
        return;
      }

      completed = true;
      afterHidden?.();
    };

    if (afterHidden != null && modalElement.classList.contains('show')) {
      modalElement.addEventListener('hidden.bs.modal', complete, { once: true });
    } else {
      complete();
    }

    modal.hide();
    setTimeout(() => {
      if (completed) {
        return;
      }

      this.forceHideModalElement(modalElement);
      complete();
    }, CLOSE_FALLBACK_MS);
  }

  private closeImportLoading(afterHidden?: () => void): void {
    if (this.importLoadingDialog == null) {
      afterHidden?.();
      return;
    }

    this.importLoadingDialog.close(afterHidden);
  }

  private blurFocusedElementInside(modalElement: HTMLElement): void {
    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && modalElement.contains(activeElement)) {
      activeElement.blur();
    }
  }

  private forceHideModalElement(modalElement: HTMLElement): void {
    modalElement.classList.remove('show');
    modalElement.setAttribute('aria-hidden', 'true');
    modalElement.removeAttribute('aria-modal');
    modalElement.removeAttribute('role');
    modalElement.style.display = 'none';
    this.removeOrphanedBackdrops();
  }

  private removeOrphanedBackdrops(): void {
    if (document.querySelector('.modal.show') != null) {
      return;
    }

    document.body.classList.remove('modal-open');
    document.body.style.removeProperty('overflow');
    document.body.style.removeProperty('padding-right');
    document.querySelectorAll('.modal-backdrop').forEach((backdrop) => backdrop.remove());
  }
}
